'use client'

import type { HoverCard as RadixHoverCard } from 'radix-ui'

import { cn } from '@morpho-org/ui-v6/utils'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@repo/ui'
import { cva } from 'class-variance-authority'
import { type ComponentProps, type PropsWithChildren, type ReactNode, useState } from 'react'

/**
 * HoverableCell trigger variants using cva for Tailwind class management.
 *
 * Matches v5 HoverableCell behavior:
 * - Base: flex container with center alignment
 * - Hover state: background changes to primary
 * - Open state: background stays primary
 * - Height: 32px minimum
 */
const hoverableCellVariants = cva(
  [
    'flex items-center',
    'bg-transparent',
    'rounded-[6px]',
    'leading-normal',
    'min-h-lg',
    'transition-colors'
  ],
  {
    variants: {
      withPadding: {
        true: ['px-[14px]'],
        false: []
      },
      withHoverEffect: {
        true: ['hover:bg-muted', 'data-[state=open]:bg-muted'],
        false: []
      }
    },
    defaultVariants: {
      withPadding: true,
      withHoverEffect: true
    }
  }
)

export interface HoverableCellProps extends PropsWithChildren {
  /** Content to display in the hover card */
  hoverCardContent: ReactNode | string
  /** Props passed to the HoverCard.Content */
  hoverCardContentProps?: ComponentProps<typeof RadixHoverCard.Content>
  /** When true, disables hover interaction */
  isDisabled?: boolean
  /** When true, makes the cell keyboard-focusable and opens the card with Enter or Space. */
  isKeyboardTrigger?: boolean
  /** When true, applies padding and enables hover background effect */
  withBackgroundEffect?: boolean
  /**
   * Custom CSS padding for the trigger (v5 parity).
   * Only applied when `withBackgroundEffect` is true; defaults to `0px 14px`.
   */
  padding?: string
  /** Enable touch/click support for mobile */
  withTouch?: boolean
  /** Additional class names for the trigger */
  className?: string
  onPointerEnter?: ComponentProps<'div'>['onPointerEnter']
  onFocus?: ComponentProps<'div'>['onFocus']
  onKeyDown?: ComponentProps<'div'>['onKeyDown']
}

/**
 * HoverableCell component - Tailwind replica of dapp-ui-v5 HoverableCell
 *
 * A cell wrapper that displays hover card content when hovered.
 *
 * Matches v5 HoverableCell behavior:
 * - Trigger has optional background effect on hover
 * - Content displays in HoverCard
 * - Disabled state renders children without hover behavior
 *
 * @example
 * ```tsx
 * <HoverableCell
 *   hoverCardContent={<div>Hover content</div>}
 *   withBackgroundEffect
 * >
 *   Cell content
 * </HoverableCell>
 * ```
 */
const HoverableCell = ({
  children,
  hoverCardContent,
  isDisabled = false,
  isKeyboardTrigger = false,
  withBackgroundEffect = true,
  padding,
  withTouch = false,
  hoverCardContentProps,
  className,
  onPointerEnter,
  onFocus,
  onKeyDown
}: HoverableCellProps) => {
  const [open, setOpen] = useState(false)

  const isInteractive = !isDisabled && !!hoverCardContent
  const isKeyboardInteractive = isKeyboardTrigger && isInteractive

  const touchProps = withTouch
    ? {
        onClick: (e: React.MouseEvent) => {
          e.preventDefault()
          e.stopPropagation()
          setOpen(prev => !prev)
        }
      }
    : {}

  const triggerElement = (
    <div
      className={cn(
        hoverableCellVariants({
          withPadding: withBackgroundEffect && !padding,
          withHoverEffect: isInteractive && withBackgroundEffect
        }),
        className,
        isKeyboardInteractive &&
          "relative after:absolute after:inset-x-0 after:-inset-y-1 after:content-['']"
      )}
      role={isKeyboardInteractive ? 'button' : undefined}
      tabIndex={isKeyboardInteractive ? 0 : undefined}
      style={withBackgroundEffect && padding ? { padding } : undefined}
      onPointerEnter={onPointerEnter}
      onFocus={onFocus}
      onKeyDown={
        isKeyboardTrigger
          ? event => {
              if (isInteractive && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault()
                setOpen(true)
              } else if (isInteractive && event.key === 'Escape') {
                setOpen(false)
              }
              onKeyDown?.(event)
            }
          : onKeyDown
      }
      {...touchProps}
    >
      {typeof children === 'string' ? (
        <span className="text-foreground text-sm leading-normal">{children}</span>
      ) : (
        children
      )}
    </div>
  )

  if (!isInteractive) return triggerElement

  return (
    <HoverCard openDelay={200} closeDelay={100} open={open} onOpenChange={setOpen}>
      <HoverCardTrigger asChild>{triggerElement}</HoverCardTrigger>

      {hoverCardContent && (
        <HoverCardContent
          side="top"
          sideOffset={8}
          onClick={e => e.stopPropagation()}
          style={{ minWidth: '275px', maxWidth: '315px' }}
          {...hoverCardContentProps}
          className={cn('rounded-[20px] p-0', hoverCardContentProps?.className)}
        >
          {hoverCardContent}
        </HoverCardContent>
      )}
    </HoverCard>
  )
}

HoverableCell.displayName = 'HoverableCell'

export { HoverableCell, hoverableCellVariants }
